"use client"

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { toast } from "sonner"

import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"
import { auditTemplateKeys } from "@/lib/apps/autoleads/keys/audit-template"

import type {
  AuditKind,
  AuditPreviewPayload,
  AuditSampleLead,
  AuditTemplate,
  AuditTemplatePayload,
  CampaignAuditTemplate,
} from "@/nango/types/apps.autoleads/autoleads/AuditTemplate"

const BASE = "api/al/audit_template/"
// PageSpeed takes 30 to 60 seconds to capture a site.
const SCREENSHOTS_POLL_INTERVAL_MS = 10_000

function reportError(err: unknown) {
  toast.error(err instanceof Error ? err.message : "Une erreur est survenue")
}

export function useAuditTemplates(teamId: number | null) {
  return useQuery({
    queryKey: auditTemplateKeys.list(teamId),
    queryFn: () =>
      apiClient<AuditTemplate[]>({
        url: `${apiUrl(BASE)}?team_id=${teamId}`,
        method: "GET",
      }),
    enabled: teamId !== null,
  })
}

export function useSaveAuditTemplate(teamId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: AuditTemplatePayload & { id?: number }) =>
      apiClient<AuditTemplate>({
        url: apiUrl(id === undefined ? BASE : `${BASE}${id}/`),
        method: id === undefined ? "POST" : "PATCH",
        data: id === undefined ? { ...payload, team_id: teamId } : payload,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: auditTemplateKeys.list(teamId) }),
    onError: reportError,
  })
}

export function useDeleteAuditTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiClient<void>({ url: apiUrl(`${BASE}${id}/`), method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: auditTemplateKeys.all }),
    onError: reportError,
  })
}

export function useUploadAuditLogo(teamId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => {
      const data = new FormData()
      data.append("file", file)
      return apiClient<AuditTemplate>({
        url: apiUrl(`${BASE}${id}/upload_logo/`),
        method: "POST",
        data,
      })
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: auditTemplateKeys.list(teamId) }),
    onError: reportError,
  })
}

// Re-reads the colours and the logo off the client's website, and saves them.
export function useAuditFromSite(teamId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiClient<AuditTemplate>({
        url: apiUrl(`${BASE}${id}/from_site/`),
        method: "POST",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: auditTemplateKeys.list(teamId) }),
    onError: reportError,
  })
}

// The draft rendered as HTML. Keyed on the whole draft, so the caller debounces
// it; the previous page stays on screen while the next one renders.
export function useAuditPreview(payload: AuditPreviewPayload | null) {
  return useQuery({
    queryKey: auditTemplateKeys.preview(payload),
    queryFn: () =>
      apiClient<{ html: string; screenshots_pending: boolean }>({
        url: apiUrl(`${BASE}preview/`),
        method: "POST",
        data: payload ?? {},
      }),
    enabled: payload !== null,
    placeholderData: keepPreviousData,
    // The lead's site screenshots are being taken: render again until they land.
    refetchInterval: (query) =>
      query.state.data?.screenshots_pending
        ? SCREENSHOTS_POLL_INTERVAL_MS
        : false,
  })
}

export function useDownloadAuditPreviewPdf() {
  return useMutation({
    mutationFn: (payload: AuditPreviewPayload) =>
      apiClient<Blob>({
        url: apiUrl(`${BASE}preview_pdf/`),
        method: "POST",
        data: payload,
        responseType: "blob",
      }),
    onError: reportError,
  })
}

export function useAuditSampleLeads(teamId: number | null) {
  return useQuery({
    queryKey: auditTemplateKeys.sampleLeads(teamId),
    queryFn: () =>
      apiClient<AuditSampleLead[]>({
        url: `${apiUrl(`${BASE}sample_leads/`)}?team_id=${teamId}`,
        method: "GET",
      }),
    enabled: teamId !== null,
  })
}

export function useCampaignAuditTemplate(campaignId: number | null) {
  return useQuery({
    queryKey: auditTemplateKeys.campaign(campaignId),
    queryFn: () =>
      apiClient<CampaignAuditTemplate>({
        url: `${apiUrl(`${BASE}campaign/`)}?campaign_id=${campaignId}`,
        method: "GET",
      }),
    enabled: campaignId !== null,
  })
}

export function useSetCampaignAuditTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CampaignAuditTemplate) =>
      apiClient<CampaignAuditTemplate>({
        url: apiUrl(`${BASE}campaign/`),
        method: "POST",
        data: payload,
      }),
    onSuccess: (data) =>
      qc.setQueryData(auditTemplateKeys.campaign(data.campaign_id), data),
    onError: reportError,
  })
}

// The address of one version of a lead's audit PDF, rendered on the spot when
// missing or stale. Asked only while the viewer is open.
export function useLeadAuditPdf(
  leadId: number,
  kind: AuditKind,
  enabled: boolean,
) {
  return useQuery({
    queryKey: auditTemplateKeys.leadPdf(leadId, kind),
    queryFn: () =>
      // `url` is the file to show in place; `link` the Grezy address to open or share.
      apiClient<{ url: string; link: string }>({
        url: `${apiUrl(`${BASE}lead_pdf/`)}?lead_id=${leadId}&kind=${kind}`,
        method: "GET",
      }),
    enabled,
    // The PDF is re-rendered server-side whenever it goes stale: ask again on each opening.
    staleTime: 0,
    retry: false,
  })
}
